import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { EarthScene } from '../components/EarthScene';
import { ORBITS, ORBIT_ORDER } from '../data/orbits';
import { useProgress } from '../hooks/useProgress';
import { ChevronRight, ChevronLeft, Lock, CheckCircle, Award, Sliders, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { PremiumModal } from '../components/PremiumModal';

// Interactive slider component for orbital elements lesson
function InteractiveSlider({ label, value, min, max, step, unit, onChange, color, description }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '14px', color: color || '#00F0FF', fontWeight: 600 }}>
          {typeof value === 'number' ? value.toFixed(step < 0.1 ? 2 : 1) : value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{ accentColor: color || '#00F0FF', background: `linear-gradient(to right, ${color || '#00F0FF'} ${pct}%, rgba(255,255,255,0.15) ${pct}%)` }}
      />
      {description && (
        <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' }}>{description}</p>
      )}
    </div>
  );
}

export default function Learn() {
  const { orbitId } = useParams();
  const navigate = useNavigate();
  const { progress, markLessonComplete, addPoints, isOrbitUnlocked, isLessonComplete, FREE_ORBITS, PREMIUM_ORBITS } = useProgress();
  const [selectedId, setSelectedId] = useState(orbitId || 'orbital_elements');
  const [stepIndex, setStepIndex] = useState(0);
  const [showLabels, setShowLabels] = useState(true);
  const [showForces, setShowForces] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  
  // Interactive parameters for orbital elements lesson
  const [interactiveParams, setInteractiveParams] = useState({
    semiMajor: 4.0,
    eccentricity: 0.3,
    inclination: 45,
    raan: 45,
    argPeriapsis: 0,
    trueAnomaly: 0,
  });

  useEffect(() => {
    if (orbitId && ORBITS[orbitId]) {
      setSelectedId(orbitId);
      setStepIndex(0);
    }
  }, [orbitId]);

  const orbit = ORBITS[selectedId];
  const steps = orbit?.lessonSteps || [];
  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const isUnlocked = isOrbitUnlocked(selectedId);
  const isComplete = isLessonComplete(selectedId);
  const isInteractiveLesson = selectedId === 'orbital_elements';

  // Create dynamic orbit for interactive lessons
  const displayOrbit = useMemo(() => {
    if (isInteractiveLesson) {
      return {
        ...orbit,
        semiMajor: interactiveParams.semiMajor,
        eccentricity: interactiveParams.eccentricity,
        inclination: interactiveParams.inclination,
        raan: interactiveParams.raan,
        speed: 0.1,
      };
    }
    return orbit;
  }, [orbit, isInteractiveLesson, interactiveParams]);

  const handleSelectOrbit = (id) => {
    navigate(`/learn/${id}`);
    setStepIndex(0);
  };

  const handleNext = () => {
    if (isLastStep) {
      if (!isComplete) {
        markLessonComplete(selectedId);
        addPoints(50);
        toast.success(`Lesson complete! +50 XP earned`, { icon: '🎓' });
      }
    } else {
      setStepIndex(s => s + 1);
    }
  };

  const handleQuiz = () => navigate(`/quiz/${selectedId}`);

  // Get the appropriate interactive control for current step
  const renderInteractiveControl = () => {
    if (!isInteractiveLesson || !currentStep?.interactive) return null;

    const type = currentStep.interactiveType;
    const color = '#00F0FF';

    switch (type) {
      case 'overview':
        return (
          <div className="grid grid-cols-2 gap-3 p-4 rounded-lg" style={{ background: 'rgba(0,240,255,0.05)', border: '1px solid rgba(0,240,255,0.2)' }}>
            <div className="text-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>SIZE</p>
              <p style={{ fontFamily: 'Rajdhani', fontSize: '14px', fontWeight: 700, color: '#00F0FF' }}>a (Semi-Major)</p>
            </div>
            <div className="text-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>SHAPE</p>
              <p style={{ fontFamily: 'Rajdhani', fontSize: '14px', fontWeight: 700, color: '#FFD700' }}>e (Eccentricity)</p>
            </div>
            <div className="text-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>TILT</p>
              <p style={{ fontFamily: 'Rajdhani', fontSize: '14px', fontWeight: 700, color: '#00FF94' }}>i (Inclination)</p>
            </div>
            <div className="text-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>ROTATION</p>
              <p style={{ fontFamily: 'Rajdhani', fontSize: '14px', fontWeight: 700, color: '#E879F9' }}>Ω (RAAN)</p>
            </div>
            <div className="text-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>ORIENTATION</p>
              <p style={{ fontFamily: 'Rajdhani', fontSize: '14px', fontWeight: 700, color: '#FB923C' }}>ω (Arg. Periapsis)</p>
            </div>
            <div className="text-center p-3 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>POSITION</p>
              <p style={{ fontFamily: 'Rajdhani', fontSize: '14px', fontWeight: 700, color: '#60A5FA' }}>ν (True Anomaly)</p>
            </div>
          </div>
        );
      case 'semiMajor':
        return (
          <div className="p-4 rounded-lg" style={{ background: 'rgba(0,240,255,0.05)', border: '1px solid rgba(0,240,255,0.2)' }}>
            <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#00F0FF', letterSpacing: '0.15em', marginBottom: '12px' }}>
              <Sliders size={12} className="inline mr-2" />TRY IT: Adjust the Semi-Major Axis
            </p>
            <InteractiveSlider
              label="Semi-Major Axis (a)"
              value={interactiveParams.semiMajor}
              min={2.5}
              max={9}
              step={0.1}
              unit=" units"
              onChange={v => setInteractiveParams(p => ({ ...p, semiMajor: v }))}
              color="#00F0FF"
              description="Watch the orbit grow/shrink as you change this value"
            />
          </div>
        );
      case 'eccentricity':
        return (
          <div className="p-4 rounded-lg" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.2)' }}>
            <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#FFD700', letterSpacing: '0.15em', marginBottom: '12px' }}>
              <Sliders size={12} className="inline mr-2" />TRY IT: Change the Eccentricity
            </p>
            <InteractiveSlider
              label="Eccentricity (e)"
              value={interactiveParams.eccentricity}
              min={0}
              max={0.85}
              step={0.01}
              unit=""
              onChange={v => setInteractiveParams(p => ({ ...p, eccentricity: v }))}
              color="#FFD700"
              description="0 = perfect circle, 0.5 = moderate ellipse, →1 = very elongated"
            />
          </div>
        );
      case 'inclination':
        return (
          <div className="p-4 rounded-lg" style={{ background: 'rgba(0,255,148,0.05)', border: '1px solid rgba(0,255,148,0.2)' }}>
            <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#00FF94', letterSpacing: '0.15em', marginBottom: '12px' }}>
              <Sliders size={12} className="inline mr-2" />TRY IT: Tilt the Orbit
            </p>
            <InteractiveSlider
              label="Inclination (i)"
              value={interactiveParams.inclination}
              min={0}
              max={180}
              step={1}
              unit="°"
              onChange={v => setInteractiveParams(p => ({ ...p, inclination: v }))}
              color="#00FF94"
              description="0° = equatorial, 90° = polar, >90° = retrograde"
            />
          </div>
        );
      case 'raan':
        return (
          <div className="p-4 rounded-lg" style={{ background: 'rgba(232,121,249,0.05)', border: '1px solid rgba(232,121,249,0.2)' }}>
            <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#E879F9', letterSpacing: '0.15em', marginBottom: '12px' }}>
              <Sliders size={12} className="inline mr-2" />TRY IT: Rotate the Orbital Plane
            </p>
            <InteractiveSlider
              label="RAAN (Ω)"
              value={interactiveParams.raan}
              min={0}
              max={360}
              step={1}
              unit="°"
              onChange={v => setInteractiveParams(p => ({ ...p, raan: v }))}
              color="#E879F9"
              description="Right Ascension of Ascending Node - rotates the orbit around Earth's axis"
            />
          </div>
        );
      case 'argPeriapsis':
      case 'trueAnomaly':
        return (
          <div className="p-4 rounded-lg" style={{ background: 'rgba(251,146,60,0.05)', border: '1px solid rgba(251,146,60,0.2)' }}>
            <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#FB923C', letterSpacing: '0.15em', marginBottom: '12px' }}>
              <Sliders size={12} className="inline mr-2" />TRY COMBINING ELEMENTS
            </p>
            <div className="grid grid-cols-2 gap-4">
              <InteractiveSlider
                label="Semi-Major"
                value={interactiveParams.semiMajor}
                min={2.5}
                max={8}
                step={0.1}
                unit="u"
                onChange={v => setInteractiveParams(p => ({ ...p, semiMajor: v }))}
                color="#00F0FF"
              />
              <InteractiveSlider
                label="Eccentricity"
                value={interactiveParams.eccentricity}
                min={0}
                max={0.7}
                step={0.01}
                unit=""
                onChange={v => setInteractiveParams(p => ({ ...p, eccentricity: v }))}
                color="#FFD700"
              />
              <InteractiveSlider
                label="Inclination"
                value={interactiveParams.inclination}
                min={0}
                max={90}
                step={1}
                unit="°"
                onChange={v => setInteractiveParams(p => ({ ...p, inclination: v }))}
                color="#00FF94"
              />
              <InteractiveSlider
                label="RAAN"
                value={interactiveParams.raan}
                min={0}
                max={360}
                step={5}
                unit="°"
                onChange={v => setInteractiveParams(p => ({ ...p, raan: v }))}
                color="#E879F9"
              />
            </div>
          </div>
        );
      case 'tle':
        return (
          <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.15em', marginBottom: '8px' }}>SAMPLE TLE FORMAT</p>
            <pre style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#00FF94', background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '6px', overflowX: 'auto', lineHeight: 1.6 }}>
{`ISS (ZARYA)
1 25544U 98067A   24001.50000000 ...
2 25544  51.6400 208.9163 0006703 ...`}
            </pre>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div className="p-2 rounded text-center" style={{ background: 'rgba(0,240,255,0.1)' }}>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>INC</p>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '12px', color: '#00F0FF' }}>51.64°</p>
              </div>
              <div className="p-2 rounded text-center" style={{ background: 'rgba(232,121,249,0.1)' }}>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>RAAN</p>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '12px', color: '#E879F9' }}>208.9°</p>
              </div>
              <div className="p-2 rounded text-center" style={{ background: 'rgba(255,215,0,0.1)' }}>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>ECC</p>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '12px', color: '#FFD700' }}>0.00067</p>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ height: '100vh', background: '#030305', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Navigation />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', marginTop: '64px' }}>
        {/* Sidebar - Orbit List */}
        <div
          style={{ width: '220px', flexShrink: 0, overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.07)', background: 'rgba(11,16,21,0.9)', padding: '20px 12px' }}
          data-testid="orbit-sidebar"
        >
          <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em', marginBottom: '12px', paddingLeft: '8px' }}>ORBIT TYPES</p>
          <div className="flex flex-col gap-1">
            {ORBIT_ORDER.map(id => {
              const o = ORBITS[id];
              const unlocked = isOrbitUnlocked(id);
              const complete = isLessonComplete(id);
              const isActive = id === selectedId;
              const isPremiumOrbit = PREMIUM_ORBITS.includes(id);
              const isFreeOrbit = FREE_ORBITS.includes(id);

              const handleClick = () => {
                if (unlocked) {
                  handleSelectOrbit(id);
                } else if (isPremiumOrbit) {
                  setShowPremiumModal(true);
                }
              };

              return (
                <button
                  key={id}
                  onClick={handleClick}
                  data-testid={`orbit-sidebar-${id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: isActive ? `1px solid ${o.color}50` : '1px solid transparent',
                    background: isActive ? `${o.color}10` : 'transparent',
                    cursor: unlocked ? 'pointer' : (isPremiumOrbit ? 'pointer' : 'not-allowed'),
                    opacity: unlocked ? 1 : 0.5,
                    textAlign: 'left',
                    width: '100%',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}
                >
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: unlocked ? o.color : (isPremiumOrbit ? '#FFD700' : 'rgba(255,255,255,0.2)'), flexShrink: 0 }} />
                  <span style={{ fontFamily: 'Rajdhani', fontWeight: isActive ? 700 : 500, fontSize: '13px', color: isActive ? o.color : unlocked ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
                    {o.shortName}
                  </span>
                  {!unlocked && isPremiumOrbit && <Crown size={11} color="#FFD700" />}
                  {!unlocked && isFreeOrbit && <Lock size={10} color="rgba(255,255,255,0.3)" />}
                  {complete && <CheckCircle size={11} color="#00FF94" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* 3D Scene */}
          <div style={{ height: isInteractiveLesson ? '50%' : '45%', flexShrink: 0, position: 'relative', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <EarthScene
              activeOrbits={[displayOrbit]}
              selectedOrbit={displayOrbit}
              height="100%"
              showLabels={showLabels}
              showForceVectors={showForces}
              cameraPosition={[0, 3, displayOrbit?.semiMajor ? displayOrbit.semiMajor * 2.2 : 10]}
            />
            {/* Orbit info overlay - compact version */}
            <div style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 10 }}>
              <div className="hud-panel px-3 py-2" data-testid="orbit-info-panel" style={{ background: 'rgba(3,3,5,0.85)', backdropFilter: 'blur(8px)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: displayOrbit?.color }} />
                  <span style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '12px', color: displayOrbit?.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {displayOrbit?.shortName}
                  </span>
                </div>
                {isInteractiveLesson ? (
                  <div className="flex gap-3 flex-wrap">
                    <div className="flex items-center gap-1">
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'rgba(255,255,255,0.35)' }}>a:</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: '#00F0FF' }}>{interactiveParams.semiMajor.toFixed(1)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'rgba(255,255,255,0.35)' }}>e:</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: '#FFD700' }}>{interactiveParams.eccentricity.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'rgba(255,255,255,0.35)' }}>i:</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: '#00FF94' }}>{interactiveParams.inclination.toFixed(0)}°</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'rgba(255,255,255,0.35)' }}>Ω:</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: '#E879F9' }}>{interactiveParams.raan.toFixed(0)}°</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-4 flex-wrap">
                    {[
                      ['ALT', displayOrbit?.altitudeRange],
                      ['VEL', displayOrbit?.velocity],
                    ].map(([label, val]) => (
                      <div key={label} className="flex items-center gap-1">
                        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>{label}:</span>
                        <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: '#00F0FF' }}>{val}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Scene controls */}
            <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10, display: 'flex', gap: '6px' }}>
              <button
                onClick={() => setShowLabels(v => !v)}
                data-testid="learn-toggle-labels"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ background: showLabels ? 'rgba(0,240,255,0.15)' : 'rgba(0,0,0,0.5)', border: `1px solid ${showLabels ? 'rgba(0,240,255,0.4)' : 'rgba(255,255,255,0.15)'}`, cursor: 'pointer', backdropFilter: 'blur(8px)', transition: 'all 0.2s' }}
              >
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: showLabels ? '#00F0FF' : 'rgba(255,255,255,0.5)', letterSpacing: '0.1em' }}>LABELS</span>
              </button>
              <button
                onClick={() => setShowForces(v => !v)}
                data-testid="learn-toggle-forces"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ background: showForces ? 'rgba(255,68,68,0.15)' : 'rgba(0,0,0,0.5)', border: `1px solid ${showForces ? 'rgba(255,68,68,0.4)' : 'rgba(255,255,255,0.15)'}`, cursor: 'pointer', backdropFilter: 'blur(8px)', transition: 'all 0.2s' }}
              >
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: showForces ? '#FF4444' : 'rgba(255,255,255,0.5)', letterSpacing: '0.1em' }}>FORCES</span>
              </button>
            </div>
            {/* Gravity well explanation */}
            {showForces && (
              <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px', zIndex: 10 }}>
                <div className="hud-panel" style={{ padding: '10px 14px', borderRadius: '8px' }}>
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: '#FF4444', letterSpacing: '0.12em', marginBottom: '4px' }}>WHY ORBIT = FREE-FALL</p>
                  <p style={{ fontFamily: 'Rajdhani', fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                    <span style={{ color: '#FF4444', fontWeight: 700 }}>Red arrow</span> = gravity pulling toward Earth.{' '}
                    <span style={{ color: '#44AAFF', fontWeight: 700 }}>Blue arrow</span> = velocity (sideways motion).
                    The satellite IS falling — but it moves sideways fast enough that Earth's surface curves away beneath it. That's orbit.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Lesson Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px 80px' }}>
            {!isUnlocked ? (
              <div className="flex flex-col items-center justify-center h-full gap-4" data-testid="locked-content">
                <Lock size={40} color="rgba(255,255,255,0.2)" />
                <p style={{ fontFamily: 'Rajdhani', fontSize: '18px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Orbit Locked
                </p>
                <p style={{ fontFamily: 'Outfit', fontSize: '14px', color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                  Earn more XP by completing quizzes to unlock this orbit type.
                </p>
              </div>
            ) : (
              <>
                {/* Step progress */}
                <div className="flex items-center gap-2 mb-6">
                  {steps.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setStepIndex(i)}
                      className={`step-indicator ${i === stepIndex ? 'active' : i < stepIndex ? 'completed' : ''}`}
                      data-testid={`step-indicator-${i}`}
                    />
                  ))}
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginLeft: '8px' }}>
                    {stepIndex + 1} / {steps.length}
                  </span>
                </div>

                {/* Step Content */}
                <div key={stepIndex} className="animate-fadeIn" data-testid="lesson-content">
                  <h2 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '26px', color: displayOrbit?.color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>
                    {steps[stepIndex]?.title}
                  </h2>
                  <p style={{ fontFamily: 'Outfit', fontSize: '16px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.8, marginBottom: '20px', whiteSpace: 'pre-line' }}>
                    {steps[stepIndex]?.content}
                  </p>

                  {/* Interactive controls for orbital elements lesson */}
                  {isInteractiveLesson && renderInteractiveControl()}

                  {/* Key facts (shown on first step for non-interactive lessons) */}
                  {stepIndex === 0 && !isInteractiveLesson && (
                    <div className="mb-6">
                      <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em', marginBottom: '12px' }}>KEY FACTS</p>
                      <div className="flex flex-col gap-2">
                        {displayOrbit?.funFacts?.slice(0, 3).map((fact, i) => (
                          <div key={i} className="flex items-start gap-3 px-4 py-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: displayOrbit.color, marginTop: '7px', flexShrink: 0 }} />
                            <span style={{ fontFamily: 'Outfit', fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>{fact}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Examples (shown on last step) */}
                  {isLastStep && displayOrbit?.examples && (
                    <div className="mb-6">
                      <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em', marginBottom: '12px' }}>REAL-WORLD EXAMPLES</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {displayOrbit.examples.map((ex, i) => (
                          <div key={i} className="px-4 py-3 rounded-lg" style={{ background: `${displayOrbit.color}08`, border: `1px solid ${displayOrbit.color}20` }}>
                            <div style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '15px', color: displayOrbit.color }}>{ex.name}</div>
                            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                              {ex.altitude} · i={ex.inclination} · {ex.year}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation buttons */}
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => setStepIndex(s => Math.max(0, s - 1))}
                    disabled={stepIndex === 0}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg"
                    style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', color: stepIndex === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', cursor: stepIndex === 0 ? 'not-allowed' : 'pointer', transition: 'color 0.2s, border-color 0.2s' }}
                    data-testid="prev-step-btn"
                  >
                    <ChevronLeft size={14} /> Prev
                  </button>

                  <div className="flex items-center gap-3">
                    {isLastStep && (
                      <button
                        onClick={handleQuiz}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg"
                        style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#FFD700', border: '1px solid rgba(255,215,0,0.3)', background: 'rgba(255,215,0,0.06)', cursor: 'pointer', transition: 'background 0.2s' }}
                        data-testid="take-quiz-btn"
                      >
                        <Award size={14} /> Take Quiz
                      </button>
                    )}
                    <button
                      onClick={handleNext}
                      className="flex items-center gap-2 px-5 py-2 rounded-lg"
                      style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.12em', color: '#000', background: orbit?.color, border: 'none', cursor: 'pointer', transition: 'opacity 0.2s', boxShadow: `0 0 20px ${orbit?.color}40` }}
                      data-testid="next-step-btn"
                    >
                      {isLastStep ? (isComplete ? 'Completed' : 'Complete +50 XP') : 'Next'}
                      {!isLastStep && <ChevronRight size={14} />}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Premium Modal */}
      <PremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
    </div>
  );
}
